import type { HistoryItemKind } from "@scribenote/brian-design-system";
import type { View } from "@views/viewsClient";
import type { TodayVisit } from "@visits/visitsClient";

export type HistoryEntry =
  | {
      type: "visit";
      id: string;
      at: string;
      visit: TodayVisit;
      note: View | null;
    }
  | { type: "view"; id: string; at: string; view: View };

export const entryKind = (entry: HistoryEntry): HistoryItemKind =>
  entry.type === "visit" ? "visit" : "document";

const newestFirst = (a: { at: string }, b: { at: string }) =>
  b.at.localeCompare(a.at);

export const buildHistory = (
  visits: TodayVisit[],
  views: View[],
): HistoryEntry[] => {
  const visitIds = new Set(visits.map((v) => v.id));
  const byVisit = new Map<string, View[]>();
  const loose: View[] = [];
  for (const view of views) {
    const visitId = view.entities.find(
      (s) => s.kind === "visit" && visitIds.has(s.id),
    )?.id;
    if (!visitId) {
      loose.push(view);
      continue;
    }
    byVisit.set(visitId, [...(byVisit.get(visitId) ?? []), view]);
  }

  const entries: HistoryEntry[] = visits.map((visit) => {
    const attached = [...(byVisit.get(visit.id) ?? [])].sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    );
    const note = attached[0] ?? null;
    loose.push(...attached.filter((view) => view !== note));
    return {
      type: "visit",
      id: `visit:${visit.id}`,
      at: visit.scheduledStart ?? visit.createdAt,
      visit,
      note,
    };
  });
  for (const view of loose)
    entries.push({
      type: "view",
      id: `view:${view.id}`,
      at: view.createdAt,
      view,
    });
  return entries.sort(newestFirst);
};

export const visitSpan = (
  visit: TodayVisit,
): { start: string | null; end: string | null } => {
  const entered = visit.stages
    .map((s) => s.enteredAt)
    .filter((t): t is string => t !== null)
    .sort();
  const completed = visit.stages
    .map((s) => s.completedAt)
    .filter((t): t is string => t !== null)
    .sort();
  if (entered.length)
    return {
      start: entered[0],
      end:
        completed.length === entered.length
          ? (completed[completed.length - 1] ?? null)
          : null,
    };
  return { start: visit.scheduledStart, end: visit.scheduledEnd };
};
